import { useDispatch, useSelector } from 'react-redux';
import VoucherCard from '../../atoms/VoucherCard';
import { IRootState } from '../../../store';
import eventVoucherService from '../../../lib/gtm/eventVoucherService';
import useImpressionAccessories from '../../../hooks/emarsys/accessories/useImpressionAccessories';
import { useRef } from 'react';
import { IBreadcrumbs } from '../Breadcrumbs';
import eventClickAccessories from '../../../helpers/emarsys/accessories/eventClickAccessories';
import useImpressionCouponService from '../../../hooks/emarsys/coupon-service/useImpressionCouponService';
import useImpressionPackageService from '../../../hooks/emarsys/package-service/useImpressionPackageService';
import eventClickCouponService from '../../../helpers/emarsys/coupon-service/eventClickCouponService';
import eventClickPackageService from '../../../helpers/emarsys/package-service/eventClickPackageService';

interface VoucherModalContentProps {
  voucherList: any;
  handleShowTerms: (params: boolean) => void;
  setShowVoucherModal: (params: boolean) => void;
  setIndexVoucher: (params: number) => void;
  serviceContent: any;
  feature?: string;
  impressionData?: {
    pageTitle: string;
    carModel: string[];
    breadcrumbItems: IBreadcrumbs[];
    sku: string;
    skuPrice: string;
  };
}

const VoucherModalContent = ({
  voucherList = null,
  handleShowTerms = (params: boolean) => {},
  setShowVoucherModal,
  setIndexVoucher,
  serviceContent,
  feature = '',
  impressionData,
}: VoucherModalContentProps) => {
  const impressionRef = useRef(null);
  const dispatch = useDispatch();
  const { userData } = useSelector((state: IRootState) => state.auth) || {};
  const formatEndDate = (endDate: any) => {
    if (endDate) {
      const date = endDate?.split(' ')[0];
      const splitDate = date.split('-');
      const correctDate = `${splitDate[1]}/${splitDate[0]}/${splitDate[2]}`;
      return new Intl.DateTimeFormat('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })?.format(new Date(correctDate));
    }
    return null;
  };

  const { categoryService } = useSelector((state: IRootState) => state.dataGTM) || {};
  const { selectedCar, selectedPackage } =
    useSelector((state: IRootState) => state.servicePackage) || {};

  const getFirstTermConditionItem = (termConditions) => {
    const firstSplit = termConditions?.split('</li>');
    const resultSplit = firstSplit?.[0]?.split('<li>');
    return resultSplit?.[1];
  };

  if (feature === 'ACCESSORIES') {
    useImpressionAccessories(
      impressionRef,
      impressionData?.pageTitle,
      impressionData?.breadcrumbItems,
      impressionData?.sku,
      impressionData?.skuPrice,
      'voucher promo',
      impressionData?.carModel
    );
  } else if (feature === 'COUPON_SERVICE') {
    useImpressionCouponService(
      impressionRef,
      impressionData?.pageTitle,
      impressionData?.breadcrumbItems,
      'voucher promo'
    );
  } else if (feature === 'PACKAGE_SERVICE') {
    useImpressionPackageService(
      impressionRef,
      impressionData?.pageTitle,
      impressionData?.breadcrumbItems,
      'voucher promo'
    );
  }

  return (
    <div
      ref={impressionRef}
      className="flex flex-col justify-center items-center gap-4 pt-2 pb-4 px-4 overflow-y-auto lg:p-6"
    >
      {voucherList?.map((item, index) => (
        <div
          key={index}
          role="button"
          aria-label="Voucher discount card"
          className="flex text-start w-full"
          onClick={() => {
            eventVoucherService(
              categoryService || selectedPackage?.bodyType,
              serviceContent?.couponPacketServiceContent?.titlePage || selectedPackage?.name,
              selectedCar?.vehicleName,
              serviceContent?.couponType,
              item?.name
            );
            if (feature === 'ACCESSORIES') {
              eventClickAccessories(
                dispatch,
                impressionData?.sku,
                impressionData?.skuPrice,
                impressionData?.pageTitle,
                impressionData?.breadcrumbItems,
                userData?.customerNo,
                'voucher promo',
                'voucher promo',
                impressionData?.carModel,
                {
                  voucher_code: item?.name,
                }
              );
            } else if (feature === 'COUPON_SERVICE') {
              eventClickCouponService(
                dispatch,
                selectedPackage,
                selectedCar,
                impressionData?.pageTitle,
                impressionData?.breadcrumbItems,
                userData?.customerNo,
                'voucher promo',
                'voucher promo',
                {
                  voucher_code: item?.name,
                }
              );
            } else if (feature === 'PACKAGE_SERVICE') {
              eventClickPackageService(
                dispatch,
                selectedPackage,
                selectedCar,
                impressionData?.pageTitle,
                impressionData?.breadcrumbItems,
                userData?.customerNo,
                'voucher promo',
                'voucher promo',
                {
                  voucher_code: item?.name,
                }
              );
            }
            setShowVoucherModal(false);
          }}
        >
          <div className="basis-32 grow-0 shrink-0 relative flex justify-center items-center bg-supportiveRed">
            <div className="ml-2">
              <p className="text-white text-sm font-semibold uppercase text-center">Diskon</p>
              <p className="text-white text-[28px] font-bold text-center">
                {item?.discountPercentage
                  ? `${Math?.floor(item?.discountPercentage)}%`
                  : item?.discountPrice?.value >= 1000000
                    ? `${item?.discountPrice?.value / 1000000}JT`
                    : `${item?.discountPrice?.value / 1000}RB`}
              </p>
            </div>

            <span className="inline-block h-[24px] w-[24px] absolute left-0 top-[50%] rotate-90 -translate-x-1/2 -translate-y-1/2 bg-white rounded-full"></span>
          </div>

          <VoucherCard className="p-4 w-full" borderColor="#E9E9E9">
            <p className="text-sm font-semibold text-reliableBlack uppercase mb-[6px] break-all inline-block">
              {item?.name}
            </p>
            <ul className="list-disc text-xs leading-relaxed text-reliableBlack flex flex-col gap-1 mb-3 pl-4">
              {item?.termsCondition && (
                <li>
                  <span
                    className="prose-li:text-xs prose-p:text-xs lg:prose-li:text-sm lg:prose-p:text-sm lg:text-sm line-clamp-2"
                    dangerouslySetInnerHTML={{
                      __html: getFirstTermConditionItem(item?.termsCondition),
                    }}
                  />
                </li>
              )}
              {item?.endDate && (
                <li>
                  <span className="prose-li:text-xs prose-p:text-xs lg:prose-li:text-sm lg:prose-p:text-sm lg:text-sm line-clamp-2">
                    Valid sampai dengan {formatEndDate(item?.endDate)}
                  </span>
                </li>
              )}
            </ul>

            <button
              type="button"
              className="text-xs text-hyperLink md:text-sm "
              onClick={() => {
                handleShowTerms(true);
                setIndexVoucher(index);
              }}
            >
              Syarat & Ketentuan
            </button>
          </VoucherCard>
        </div>
      ))}
    </div>
  );
};

export default VoucherModalContent;
